import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import type { CollabAutomation, CollabTriggerType, SmartCollabGroupWithCounts } from "@unbox/api";
import { handleError } from "@/api/error";
import { queryClient } from "@/providers/QueryClientProviderWrapper";
import type { TargetCollaborationListItem } from "./collaborations";
import { client, useScopedMerchantClient } from "./scoped-merchant-client";

// Mirrors GROUP_MEMBERS_PAGE_SIZE in collaborations.ts (useGroupCollaborationsPaginated).
const GROUP_COLLABORATIONS_PAGE_SIZE = 50;

export const useSmartCollabGroups = (search?: string) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["smart-collab-groups", scope.merchantId, scope.workspaceId, search],
    queryFn: async (): Promise<SmartCollabGroupWithCounts[]> => {
      if (!scope.merchantId) return [];

      const query = { search: search || undefined };
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["smart-collab-groups"].$get({
            param: scope.param,
            query,
          })
        : await client.merchant[":merchantId"]["smart-collab-groups"].$get({ param: scope.param, query });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId,
  });
};

type SmartCollabGroupCollaborationsPage = { collaborations: TargetCollaborationListItem[]; total: number };

// Child target collaborations of a persisted Smart Collab Group (UNB-1978). Infinite-scroll
// paginated, mirroring useGroupCollaborationsPaginated (collaborations.ts) for the legacy
// hash-grouped collabs - the card's scroll sentinel calls fetchNextPage until every child has
// loaded, so a group with more than one page never silently drops members.
export const useSmartCollabGroupCollaborations = (groupId: string, enabled: boolean) => {
  const scope = useScopedMerchantClient();

  return useInfiniteQuery({
    queryKey: ["smart-collab-group-collaborations", scope.merchantId, scope.workspaceId, groupId],
    queryFn: async ({ pageParam }): Promise<SmartCollabGroupCollaborationsPage> => {
      if (!scope.merchantId) return { collaborations: [], total: 0 };

      const query = { limit: String(GROUP_COLLABORATIONS_PAGE_SIZE), offset: String(pageParam) };
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["smart-collab-groups"][
            ":id"
          ].collaborations.$get({
            param: { ...scope.param, id: groupId },
            query,
          })
        : await client.merchant[":merchantId"]["smart-collab-groups"][":id"].collaborations.$get({
            param: { ...scope.param, id: groupId },
            query,
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId && enabled,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.collaborations.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
  });
};

type AutomationOverrideInput = { enabled: boolean; sequenceId: string | null };

export type CreateSmartCollabGroupPayload = {
  name: string;
  activeUntil: string;
  products: { productId: string; commissionRate: number; shopAdsCommissionRate?: number | null }[];
  creatorOpenIds: string[];
  creatorCohorts?: { tiktokCreatorOpenId: string; cohortId: string | null; cohortName: string | null }[];
  hasFreeSample: boolean;
  isSampleApprovalExempt: boolean;
  minRequirements?: {
    minGmv?: number;
    minFollowers?: number;
    minPostRate?: number;
    minAvgViews?: number;
    categoryFit?: string;
  };
  sellerEmail: string;
  invitationMessage?: string;
  whatsappNumber?: string;
  automations: Partial<Record<CollabTriggerType, AutomationOverrideInput>>;
};

// Mirrors useCreateTargetCollaboration (collaborations.ts) - same scope/error/invalidate pattern.
export const useCreateSmartCollabGroup = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async (payload: CreateSmartCollabGroupPayload) => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["smart-collab-groups"].$post({
            param: scope.param,
            json: payload,
          })
        : await client.merchant[":merchantId"]["smart-collab-groups"].$post({
            param: scope.param,
            json: payload,
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      if (scope.merchantId) {
        queryClient.invalidateQueries({ queryKey: ["smart-collab-groups", scope.merchantId] });
      }
    },
  });
};

type UpdateSmartCollabGroupPayload = Partial<{ isPinned: boolean }>;

// Mirrors useCreateSmartCollabGroup - same scope/error/invalidate pattern, PATCH instead of POST.
export const useUpdateSmartCollabGroup = (groupId: string) => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async (payload: UpdateSmartCollabGroupPayload) => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["smart-collab-groups"][":id"].$patch({
            param: { ...scope.param, id: groupId },
            json: payload,
          })
        : await client.merchant[":merchantId"]["smart-collab-groups"][":id"].$patch({
            param: { ...scope.param, id: groupId },
            json: payload,
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      if (scope.merchantId) {
        queryClient.invalidateQueries({ queryKey: ["smart-collab-groups", scope.merchantId] });
      }
    },
  });
};

export const usePinSmartCollabGroupCollaboration = (groupId: string) => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async (payload: { collaborationId: string; isPinned: boolean }) => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["smart-collab-groups"][":id"].collaborations[
            ":collaborationId"
          ].$patch({
            param: { ...scope.param, id: groupId, collaborationId: payload.collaborationId },
            json: { isPinned: payload.isPinned },
          })
        : await client.merchant[":merchantId"]["smart-collab-groups"][":id"].collaborations[":collaborationId"].$patch({
            param: { ...scope.param, id: groupId, collaborationId: payload.collaborationId },
            json: { isPinned: payload.isPinned },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      if (scope.merchantId) {
        queryClient.invalidateQueries({ queryKey: ["smart-collab-groups", scope.merchantId] });
        queryClient.invalidateQueries({
          queryKey: ["smart-collab-group-collaborations", scope.merchantId, scope.workspaceId, groupId],
        });
      }
    },
  });
};

export const useUpsertSmartCollabGroupSequenceOverride = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async ({
      groupId,
      triggerType,
      enabled,
      sequenceId,
    }: {
      groupId: string;
      triggerType: CollabTriggerType;
      enabled: boolean;
      sequenceId?: string | null;
    }) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["smart-collab-groups"][":id"].sequences[
            ":triggerType"
          ].$put({
            param: { ...scope.param, id: groupId, triggerType },
            json: { enabled, sequenceId: sequenceId ?? null },
          })
        : await client.merchant[":merchantId"]["smart-collab-groups"][":id"].sequences[":triggerType"].$put({
            param: { ...scope.param, id: groupId, triggerType },
            json: { enabled, sequenceId: sequenceId ?? null },
          });
      if (!res.ok) throw await handleError(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["smart-collab-groups", scope.merchantId] });
      queryClient.invalidateQueries({ queryKey: ["collab-automations", scope.merchantId] });
    },
  });
};

export const useDeleteSmartCollabGroupSequenceOverride = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async ({ groupId, triggerType }: { groupId: string; triggerType: CollabTriggerType }) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["smart-collab-groups"][":id"].sequences[
            ":triggerType"
          ].$delete({ param: { ...scope.param, id: groupId, triggerType } })
        : await client.merchant[":merchantId"]["smart-collab-groups"][":id"].sequences[":triggerType"].$delete({
            param: { ...scope.param, id: groupId, triggerType },
          });
      if (!res.ok) throw await handleError(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["smart-collab-groups", scope.merchantId] });
      queryClient.invalidateQueries({ queryKey: ["collab-automations", scope.merchantId] });
    },
  });
};

export const useSmartCollabGroupAutomations = (groupId: string | undefined, opts?: { enabled?: boolean }) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["collab-automations", scope.merchantId, scope.workspaceId, "group", groupId],
    queryFn: async (): Promise<CollabAutomation[]> => {
      if (!groupId) throw new Error("No group selected");
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["smart-collab-groups"][":id"].automations.$get(
            { param: { ...scope.param, id: groupId } },
          )
        : await client.merchant[":merchantId"]["smart-collab-groups"][":id"].automations.$get({
            param: { ...scope.param, id: groupId },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: (opts?.enabled ?? true) && !!scope.merchantId && !!groupId,
  });
};
